import type { MessageReasoning, ReasoningLoopRound } from '../components/ReasoningPanel';

type ReasoningPhase =
  | 'plan'
  | 'react'
  | 'tool'
  | 'observe'
  | 'sql'
  | 'critic'
  | 'summary'
  | 'thinking'
  | 'exploring';

type ToolLayer = 'L1' | 'L2';

export function emptyReasoning(): MessageReasoning {
  return {
    plan: '',
    tool: '',
    observe: '',
    sql: '',
    critic: '',
    summary: '',
    thinking: '',
    exploring: '',
    summarySteps: [],
    loopRounds: [],
  };
}

function inferLayerFromText(text: string, explicit?: string): ToolLayer | undefined {
  if (explicit === 'L1' || explicit === 'L2') return explicit;
  if (/→\s*data\./.test(text) || /准备调用\s*data\./.test(text)) return 'L2';
  if (/→\s*\w+\.\w+/.test(text) || /准备调用\s*\w+\.\w+/.test(text)) return 'L1';
  return undefined;
}

function patchRoundLayer(round: ReasoningLoopRound, layer?: ToolLayer): ReasoningLoopRound {
  if (!layer) return round;
  return { ...round, toolLayer: layer };
}

function patchLoopRoundThinkingExploring(
  rounds: ReasoningLoopRound[],
  round: number,
  phase: 'thinking' | 'exploring',
  delta: string,
  streaming: boolean,
  layer?: ToolLayer
): ReasoningLoopRound[] {
  const next = ensureRound(rounds, round);
  const idx = round - 1;
  const cur = next[idx];
  const key = phase;
  const streamKey = `${phase}Streaming` as const;
  let updated: ReasoningLoopRound = {
    ...cur,
    round,
    [key]: `${cur[key] || ''}${delta}`,
    [streamKey]: streaming,
  };
  updated = patchRoundLayer(updated, layer ?? inferLayerFromText(delta));
  next[idx] = updated;
  return next;
}

function patchLoopRoundPlan(
  rounds: ReasoningLoopRound[],
  round: number,
  delta: string,
  streaming: boolean
): ReasoningLoopRound[] {
  const next = ensureRound(rounds, round);
  const idx = round - 1;
  const cur = next[idx];
  const isBoilerplate =
    /第 \d+ 步/.test(delta) ||
    /分析中/.test(delta) ||
    /选择工具/.test(delta) ||
    /模型决策/.test(delta) ||
    /去重/.test(delta);
  if (isBoilerplate) {
    return patchLoopRoundThinkingExploring(next, round, 'exploring', delta, streaming);
  }
  return patchLoopRoundThinkingExploring(next, round, 'thinking', delta, streaming);
}

export function stopReasoningStreaming(reasoning: MessageReasoning): MessageReasoning {
  const loopRounds = (reasoning.loopRounds ?? []).map((r) => ({
    ...r,
    planStreaming: false,
    toolStreaming: false,
    observeStreaming: false,
    sqlStreaming: false,
    criticStreaming: false,
    thinkingStreaming: false,
    exploringStreaming: false,
  }));
  return {
    ...reasoning,
    loopRounds,
    planStreaming: false,
    toolStreaming: false,
    observeStreaming: false,
    sqlStreaming: false,
    criticStreaming: false,
    summaryStreaming: false,
    thinkingStreaming: false,
    exploringStreaming: false,
  };
}

function ensureRound(rounds: ReasoningLoopRound[], round: number): ReasoningLoopRound[] {
  const next = [...rounds];
  while (next.length < round) {
    next.push({ round: next.length + 1 });
  }
  const idx = round - 1;
  if (!next[idx]) next[idx] = { round };
  else if (!next[idx].round) next[idx] = { ...next[idx], round };
  return next;
}

function patchLoopRound(
  rounds: ReasoningLoopRound[],
  round: number,
  phase: 'tool' | 'observe' | 'sql' | 'critic',
  delta: string,
  streaming: boolean,
  layer?: ToolLayer
): ReasoningLoopRound[] {
  const exploringPhase = phase === 'tool' || phase === 'observe';
  if (exploringPhase) {
    return patchLoopRoundThinkingExploring(
      rounds,
      round,
      'exploring',
      delta,
      streaming,
      layer
    );
  }
  const next = ensureRound(rounds, round);
  const idx = round - 1;
  const cur = next[idx];
  if (phase === 'sql') {
    next[idx] = {
      ...cur,
      round,
      sql: `${cur.sql || ''}${delta}`,
      sqlStreaming: streaming,
    };
    return next;
  }
  next[idx] = {
    ...cur,
    round,
    critic: `${cur.critic || ''}${delta}`,
    criticStreaming: streaming,
  };
  return next;
}

function mapLegacyPhase(phase: ReasoningPhase): ReasoningPhase {
  if (phase === 'react' || phase === 'plan' || phase === 'sql') return phase;
  if (phase === 'tool' || phase === 'observe') return 'exploring';
  return phase;
}

export function applyReasoningDelta(
  reasoning: MessageReasoning,
  phase: ReasoningPhase,
  delta: string,
  round: number | undefined,
  implicitRound: number,
  layer?: ToolLayer
): { reasoning: MessageReasoning; implicitRound: number } {
  const mapped = mapLegacyPhase(phase);

  if (mapped === 'thinking') {
    if (round != null) {
      return {
        implicitRound,
        reasoning: {
          ...reasoning,
          loopRounds: patchLoopRoundThinkingExploring(
            reasoning.loopRounds ?? [],
            round,
            'thinking',
            delta,
            true,
            layer
          ),
          thinkingStreaming: true,
        },
      };
    }
    return {
      implicitRound,
      reasoning: {
        ...reasoning,
        thinking: (reasoning.thinking || '') + delta,
        thinkingStreaming: true,
      },
    };
  }

  if (mapped === 'exploring') {
    if (round != null) {
      return {
        implicitRound,
        reasoning: {
          ...reasoning,
          loopRounds: patchLoopRoundThinkingExploring(
            reasoning.loopRounds ?? [],
            round,
            'exploring',
            delta,
            true,
            layer
          ),
          exploringStreaming: true,
        },
      };
    }
    return {
      implicitRound,
      reasoning: {
        ...reasoning,
        exploring: (reasoning.exploring || '') + delta,
        exploringStreaming: true,
      },
    };
  }

  if (phase === 'plan' || phase === 'react') {
    if (round != null) {
      return {
        implicitRound,
        reasoning: {
          ...reasoning,
          loopRounds: patchLoopRoundPlan(reasoning.loopRounds ?? [], round, delta, true),
          planStreaming: true,
        },
      };
    }
    return {
      implicitRound,
      reasoning: { ...reasoning, plan: reasoning.plan + delta, planStreaming: true },
    };
  }
  if (phase === 'summary') {
    return {
      implicitRound,
      reasoning: { ...reasoning, summary: reasoning.summary + delta, summaryStreaming: true },
    };
  }

  if (phase === 'sql') {
    const activeRound = round ?? (implicitRound || 1);
    const loopRounds = patchLoopRound(
      reasoning.loopRounds ?? [],
      activeRound,
      'sql',
      delta,
      true,
      layer
    );
    return {
      implicitRound: Math.max(implicitRound, activeRound),
      reasoning: {
        ...reasoning,
        loopRounds,
        sql: reasoning.sql + delta,
        sqlStreaming: true,
      },
    };
  }

  const loopPhase = phase as 'tool' | 'observe' | 'critic';
  let activeRound = round;
  let nextImplicit = implicitRound;

  if (activeRound == null) {
    if (loopPhase === 'observe' && !reasoning.observeStreaming) {
      nextImplicit += 1;
    }
    activeRound = loopPhase === 'tool' && nextImplicit === 0 ? undefined : nextImplicit || 1;
  } else if (loopPhase === 'observe') {
    nextImplicit = Math.max(nextImplicit, activeRound);
  }

  if (activeRound == null) {
    return {
      implicitRound: nextImplicit,
      reasoning: {
        ...reasoning,
        tool: reasoning.tool + delta,
        toolStreaming: true,
      },
    };
  }

  const loopRounds = patchLoopRound(
    reasoning.loopRounds ?? [],
    activeRound,
    loopPhase,
    delta,
    true,
    layer
  );
  return {
    implicitRound: nextImplicit,
    reasoning: {
      ...reasoning,
      loopRounds,
      observe: loopPhase === 'observe' ? reasoning.observe + delta : reasoning.observe,
      critic: loopPhase === 'critic' ? reasoning.critic + delta : reasoning.critic,
      tool: loopPhase === 'tool' && activeRound === 0 ? reasoning.tool + delta : reasoning.tool,
      observeStreaming: loopPhase === 'observe' ? true : reasoning.observeStreaming,
      criticStreaming: loopPhase === 'critic' ? true : reasoning.criticStreaming,
      toolStreaming: loopPhase === 'tool' && activeRound == null ? true : reasoning.toolStreaming,
      exploringStreaming: loopPhase === 'tool' || loopPhase === 'observe' ? true : reasoning.exploringStreaming,
    },
  };
}

function clearRoundStreaming(
  round: ReasoningLoopRound,
  phase: ReasoningPhase
): ReasoningLoopRound {
  if (phase === 'thinking') return { ...round, thinkingStreaming: false };
  if (phase === 'exploring') return { ...round, exploringStreaming: false };
  if (phase === 'plan' || phase === 'react') return { ...round, planStreaming: false };
  if (phase === 'tool') return { ...round, toolStreaming: false, exploringStreaming: false };
  if (phase === 'observe') return { ...round, observeStreaming: false, exploringStreaming: false };
  if (phase === 'sql') return { ...round, sqlStreaming: false };
  if (phase === 'critic') return { ...round, criticStreaming: false };
  return round;
}

export function applyReasoningDone(
  reasoning: MessageReasoning,
  phase: ReasoningPhase,
  round?: number
): MessageReasoning {
  const mapped = mapLegacyPhase(phase);

  if (mapped === 'thinking' || mapped === 'exploring') {
    if (round != null && reasoning.loopRounds?.length) {
      const loopRounds = reasoning.loopRounds.map((r) =>
        r.round === round ? clearRoundStreaming(r, mapped) : r
      );
      return {
        ...reasoning,
        loopRounds,
        thinkingStreaming: mapped === 'thinking' ? false : reasoning.thinkingStreaming,
        exploringStreaming: mapped === 'exploring' ? false : reasoning.exploringStreaming,
      };
    }
    return {
      ...reasoning,
      thinkingStreaming: mapped === 'thinking' ? false : reasoning.thinkingStreaming,
      exploringStreaming: mapped === 'exploring' ? false : reasoning.exploringStreaming,
    };
  }

  if (phase === 'plan' || phase === 'react') {
    if (round != null && reasoning.loopRounds?.length) {
      const loopRounds = reasoning.loopRounds.map((r) =>
        r.round === round ? clearRoundStreaming(r, phase) : r
      );
      return { ...reasoning, loopRounds, planStreaming: false };
    }
    return { ...reasoning, planStreaming: false };
  }
  if (phase === 'summary') return { ...reasoning, summaryStreaming: false };
  if (phase === 'sql') {
    if (round != null && reasoning.loopRounds?.length) {
      const loopRounds = reasoning.loopRounds.map((r) =>
        r.round === round ? clearRoundStreaming(r, 'sql') : r
      );
      return { ...reasoning, loopRounds, sqlStreaming: false };
    }
    return { ...reasoning, sqlStreaming: false };
  }

  const loopPhase = phase as 'tool' | 'observe' | 'critic';
  if (round != null && reasoning.loopRounds?.length) {
    const loopRounds = reasoning.loopRounds.map((r) =>
      r.round === round ? clearRoundStreaming(r, loopPhase) : r
    );
    return {
      ...reasoning,
      loopRounds,
      observeStreaming: loopPhase === 'observe' ? false : reasoning.observeStreaming,
      criticStreaming: loopPhase === 'critic' ? false : reasoning.criticStreaming,
      toolStreaming: loopPhase === 'tool' && round == null ? false : reasoning.toolStreaming,
      exploringStreaming: loopPhase === 'tool' || loopPhase === 'observe' ? false : reasoning.exploringStreaming,
    };
  }

  if (phase === 'tool') return { ...reasoning, toolStreaming: false, exploringStreaming: false };
  if (phase === 'observe') return { ...reasoning, observeStreaming: false, exploringStreaming: false };
  return { ...reasoning, criticStreaming: false };
}
